import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axiosClient from "../api/axiosClient";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (e) {
      console.error("Invalid user data in localStorage", e);
      localStorage.removeItem("user");
      return null;
    }
  });

  // 🔁 Recharge depuis localStorage au cas où (sécurité)
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (e) {
      console.error("Invalid user data in localStorage", e);
      localStorage.removeItem("user");
    }
  }, []);

  // Connexion = enregistrement user dans context (Sprint 2: token is in httpOnly cookie)
  const login = useCallback((userData, _token) => {
    localStorage.setItem("user", JSON.stringify(userData));
    // Sprint 2: access token no longer stored in localStorage — httpOnly cookie
    setUser(userData);
  }, []);

  // 🔄 Rafraîchit les infos user depuis /me/ (utile quand le backend ajoute
  // de nouveaux champs — ex. etablissement_nom/ville — et que le user en
  // cache localStorage est antérieur au changement).
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axiosClient.get("/me/");
      if (data && data.id) {
        localStorage.setItem("user", JSON.stringify(data));
        setUser(data);
        return data;
      }
    } catch (e) {
      // silencieux : on garde le cache existant si l'appel échoue
    }
    return null;
  }, []);

  // Au démarrage : si un user est connecté mais qu'il lui manque les
  // métadonnées établissement (cache pré-migration), on refetch une fois.
  useEffect(() => {
    try {
      const stored = localStorage.getItem("user");
      if (!stored) return;
      const parsed = JSON.parse(stored);
      const needsRefresh =
        parsed &&
        parsed.etablissement &&
        typeof parsed.etablissement_nom === "undefined";
      if (needsRefresh) {
        refreshUser();
      }
    } catch {
      /* ignore */
    }
  }, [refreshUser]);

  // 🔒 Déconnexion = suppression des données (audit F-7: nettoyage complet)
  const logout = useCallback(() => {
    // Effacer tous les artefacts connus
    try { localStorage.removeItem("user"); } catch {}
    try { localStorage.removeItem("token"); } catch {}
    try { localStorage.removeItem("refresh"); } catch {}
    try { localStorage.removeItem("session_id"); } catch {}
    try { localStorage.removeItem("mfa_ticket"); } catch {}
    try { localStorage.removeItem("mfa_username"); } catch {}
    // Nettoyage plus large: toute clé liée à l'auth/app
    try {
      const keys = Object.keys(localStorage);
      for (const k of keys) {
        if (/^(token|refresh|user|auth|session|mfa_|jwt)/i.test(k)) {
          try { localStorage.removeItem(k); } catch {}
        }
      }
    } catch {}
    try { sessionStorage.clear(); } catch {}
    setUser(null);
  }, []);

  // 🎯 Récupération du rôle si connecté
  const role = user?.role || null;

  const value = useMemo(() => ({ user, role, login, logout, refreshUser }), [user, role, login, logout, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ✅ Hook d’accès rapide au contexte
export function useAuth() {
  return useContext(AuthContext);
}
